import React, { useEffect, useState } from 'react';
import { FiGift } from 'react-icons/fi';
import { AMBASSADOR_EVENT, getAmbassador, redeemAmbassador } from '../ambassadorApi';
import { useLanguage } from '../contexts/LanguageContext';

const ARABIC_CODE_ERRORS = {
  'Enter a valid ambassador code.': 'أدخل رمز سفير صالحاً.',
  'That ambassador code is invalid or inactive.': 'رمز السفير غير صحيح أو غير مفعّل.',
  'Your account is already linked to an ambassador and cannot be changed.': 'حسابك مرتبط بسفير بالفعل ولا يمكن تغييره.',
  'Ambassador codes are not available yet.': 'رموز السفراء غير متاحة حالياً.',
  'Your ambassador benefit is being updated. Please try again shortly.': 'جارٍ تحديث مزايا السفير. حاول بعد قليل.',
};

export default function AmbassadorPanel({ user }) {
  const { language } = useLanguage();
  const ar = language === 'ar';
  const [status, setStatus] = useState(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    if (!user?.getIdToken) return undefined;
    let mounted = true;
    const refresh = () => getAmbassador(user).then((value) => { if (mounted) setStatus(value); }).catch(() => {
      if (mounted) setError(ar ? 'تعذّر تحميل معلومات السفير. أعد تحميل الصفحة للمحاولة مجدداً.' : 'Could not load ambassador details. Reload the page to try again.');
    });
    refresh();
    window.addEventListener(AMBASSADOR_EVENT, refresh);
    return () => { mounted = false; window.removeEventListener(AMBASSADOR_EVENT, refresh); };
  }, [user, ar]);
  const submit = async (event) => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError('');
    try { setStatus(await redeemAmbassador(user, code)); }
    catch (err) { setError(ar ? ARABIC_CODE_ERRORS[err.message] || 'تعذّر تطبيق رمز السفير حالياً. حاول مجدداً.' : err.message); }
    finally { setBusy(false); }
  };
  if (!status?.enabled && !status?.redeemed && !error) return null;
  return (
    <section className="js-profile-card js-ambassador-panel" aria-labelledby="ambassador-title">
      <div className="js-profile-card__head"><h2 id="ambassador-title">{ar ? 'رمز السفير' : 'Ambassador code'}</h2><FiGift aria-hidden="true" /></div>
      {status?.redeemed ? (
        <div role="status">
          <p>{ar ? 'سفيرك:' : 'Your ambassador:'} <strong><bdi>{status.ambassadorName}</bdi></strong> (<bdi dir="ltr">{status.code}</bdi>)</p>
          <p>{status.discountAvailable
            ? (ar ? 'خصم 50% محفوظ لشهرك المدفوع التالي، مهما انتظرت قبل الشراء.' : 'Your 50% discount is saved for your next paid month, whenever you choose to buy.')
            : (ar ? 'لقد استخدمت خصم السفير.' : 'You’ve used your ambassador discount.')}</p>
          <small>{ar ? 'ارتباطك بهذا السفير دائم ولا يمكن تغييره.' : 'Your ambassador connection is permanent and cannot be changed.'}</small>
        </div>
      ) : status?.enabled ? (
        <>
          <p className="js-ambassador-panel__intro">{ar ? 'لديك رمز من أحد سفرائنا؟ احصل على خصم 50% على شهر واحد.' : 'Have a code from an ambassador? Save 50% on one month.'}</p>
          <form id="profile-referral" className="js-ambassador-panel__form" onSubmit={submit}>
            <label htmlFor="profile-ambassador-code">{ar ? 'رمز السفير' : 'Ambassador code'}</label>
            <div className="js-ambassador-panel__entry">
              <input id="profile-ambassador-code" dir="ltr" value={code} onChange={(e) => setCode(e.target.value)}
                aria-describedby="profile-referral-help" placeholder={ar ? 'أدخل الرمز' : 'Enter your code'}
                maxLength={64} autoCapitalize="none" spellCheck={false} disabled={busy} required />
              <button className="js-ambassador-panel__apply" type="submit" disabled={busy || !code.trim()}>{busy ? (ar ? 'جارٍ التطبيق…' : 'Applying…') : (ar ? 'تطبيق الرمز' : 'Apply code')}</button>
            </div>
            <small id="profile-referral-help">{ar ? 'خصمك محفوظ حتى تستخدمه. بعد تطبيق الرمز، لا يمكنك تغيير السفير.' : 'Your discount stays available until you use it. Once applied, your ambassador can’t be changed.'}</small>
          </form>
        </>
      ) : null}
      {error && <p role="alert" className="js-subscription-panel__error">{error}</p>}
    </section>
  );
}
